import React, { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import pbPortal, { pb } from '../lib/pb';
import { useToast } from '../shared/components/ToastProvider';

// Client portal login (E1). Password sign-in for clients who set one
// (migration 003 self-service) + "send me a sign-in link" for everyone
// else — the OTP magic-link era (docs/auth_magic_link_spec.md) means most
// clients never have a password, so the email-link path is the primary
// recovery route here (admin "Resend access" is the other one).
export default function Login() {
  const { tenantSlug } = useParams();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  // Email-link panel (toggled open below the password form).
  const [linkMode, setLinkMode] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // Already signed in as a client → straight to the portal.
  if (pbPortal.authStore.isValid && pbPortal.authStore.record?.role === 'client') {
    const slug = tenantSlug || '';
    return <Navigate to={`/portal/${slug}`} replace />;
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const auth = await pbPortal.collection('accounts').authWithPassword(email.trim(), password);

      if (auth.record.role !== 'client') {
        // Agency accounts must use the admin login — keep the stores clean.
        pbPortal.authStore.clear();
        notify('Agency accounts sign in at /admin/login', 'error');
        return;
      }

      notify('Welcome back');
      // Resolve the tenant slug from the account when not in the URL.
      let slug = tenantSlug || '';
      if (!slug) {
        try {
          const tenants = await pb.collection('tenants').getFullList();
          const match = tenants.find((t) => t.id === auth.record.tenant_id);
          slug = match?.slug || '';
        } catch {
          slug = '';
        }
      }
      navigate(`/portal/${slug}`, { replace: true });
    } catch {
      notify('Invalid email or password', 'error');
    } finally {
      setBusy(false);
    }
  }

  // Email a fresh OTP magic link. Always report "sent" (PB returns a
  // dummy response for unknown emails — no enumeration leaks).
  async function sendLink(event) {
    event.preventDefault();
    if (!linkEmail.trim() || linkBusy) return;
    setLinkBusy(true);
    try {
      await pbPortal.collection('accounts').requestOTP(linkEmail.trim());
    } catch {
      // treated as sent, see above
    } finally {
      setLinkBusy(false);
      setLinkSent(true);
    }
  }

  return (
    <div className="standalone-page login-page">
      <section className="panel settings-form login-card">
        <div className="brand admin-brand login-brand">
          <div className="brand-mark"><span>PP</span></div>
        </div>
        <h1>Client Portal</h1>
        <p className="eyebrow">Sign in to view your jobs, invoices and messages</p>

        {!linkSent ? (
          <>
            {!linkMode && (
              <form onSubmit={submit}>
                <label>Email
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </label>
                <label>Password
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </label>
                <button className="primary-button" disabled={busy}>
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
            )}

            {linkMode && (
              <form onSubmit={sendLink}>
                <label>Email
                  <input
                    type="email"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="username"
                  />
                </label>
                <button className="primary-button" disabled={linkBusy}>
                  {linkBusy ? 'Sending…' : 'Send me a sign-in link'}
                </button>
              </form>
            )}

            <button
              type="button"
              className="link-button"
              onClick={() => setLinkMode((v) => !v)}
            >
              {linkMode ? 'Sign in with a password instead' : 'No password? Email me a sign-in link'}
            </button>
            <p className="login-hint">Sign-in links expire after 10 minutes.</p>
          </>
        ) : (
          <div className="send-ok">
            <p>If that email has a portal account, a sign-in link is on its way — check your inbox (valid for 10 minutes).</p>
            <button className="primary-button" onClick={() => { setLinkSent(false); setLinkMode(false); }}>
              Back to sign in
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
